import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { setDirtyForm } from 'app/actions/dirty-forms.actions';
import { checkAccess } from 'app/actions/app.actions';
import { fetchLotteryContacts, fetchInquiryContacts } from 'contact/actions/contact.actions';
import template from './create-message.html';
import * as api from '../../resources/message.api';

const DIRTY_FORM = 'createMessage';
const SELECTED_RETAILERS = 'SelectedRetailers';
const UNTRACKED = 'Untracked';
export const SELECTED_USERS = 'SelectedUsers';

const mapStateToThis = ({ session, config, contact }) => {
  const { locale, emailAddress } = session;
  const { recipientCategories, ckeditor } = config;
  const ckeditorCreateMessage = {
    extraPlugins: 'wordcount',
    wordcount: {
      showParagraphs: false,
      showWordCount: false,
      showCharCount: true,
      countSpacesAsChars: true,
      countHTML: true,
      maxCharCount: 1024,
    },
  };
  const ckeditorConfig = { ...ckeditor, ...ckeditorCreateMessage };

  return {
    locale,
    emailAddress,
    recipientCategories: recipientCategories.filter((recipientCategory) => recipientCategory.categoryCode !== UNTRACKED),
    ckeditorConfig,
    contact,
  };
};
const mapDispatchToThis = {
  setDirtyForm,
  checkAccess,
  fetchLotteryContacts,
  fetchInquiryContacts,
};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', '$state', 'Notifications', 'messageDefinitionService')
class CreateMessageController {
  constructor() {
    this.DIRTY_FORM = DIRTY_FORM;
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
  }

  async $onInit() {
    this.canCreateMessage = this.checkAccess('message.create');
    this.canCreateMessageLimitToRetailers = this.checkAccess('message.create.limitToRetailers');
    this.newMessageModel = {
      replyEmailAddress: this.emailAddress,
      recipients: [],
      retailerIds: [],
      allowReply: false,
    };
    await this.fetchContacts();
    this.setReplyEmailList();
  }

  $onDestroy() {
    this.unsubscribe();
  }

  async fetchContacts() {
    await Promise.all([this.fetchLotteryContacts(), this.fetchInquiryContacts()]);
  }

  setReplyEmailList() {
    const contactsEmailList = this.getEmailListFromContacts();
    contactsEmailList.add(this.emailAddress);
    this.replyEmailList = Array.from(contactsEmailList);
  }

  getEmailListFromContacts() {
    const replyEmailList = new Set();
    get(this.contact, `inquiry.items[${this.locale}]`, []).forEach(({ contactEmail }) => {
      if (contactEmail) replyEmailList.add(contactEmail);
    });
    get(this.contact, 'lottery.contactList.groups', []).forEach((group) => {
      get(group, 'entries', []).forEach((entry) => {
        get(entry, `fields[${this.locale}].email`, []).forEach(({ email }) => {
          if (email) replyEmailList.add(email);
        });
      });
    });

    return replyEmailList;
  }

  onChangeRecipientCategory() {
    this.newMessageModel.recipients = [];
    this.newMessageModel.retailerIds = [];
  }

  onChangeAllowReply() {
    this.newMessageModel.replyEmailAddress = this.newMessageModel.allowReply ? this.newMessageModel.replyEmailAddress : this.emailAddress;
  }

  isEmailRequired() {
    return this.newMessageModel.recipients.length === 0;
  }

  isRecipientTypeByRetailer() {
    return this.newMessageModel.recipientCategoryCode === SELECTED_RETAILERS;
  }

  isRecipientTypeManual() {
    return this.newMessageModel.recipientCategoryCode === SELECTED_USERS;
  }

  addRetailerId() {
    this.checkDuplicateRetailerId();
    if (!this.newMessageModel.retailer || this.form.retailer.$invalid) {
      return;
    }
    this.newMessageModel.retailerIds = this.newMessageModel.retailerIds.concat(this.newMessageModel.retailer);
    this.newMessageModel.retailer = '';
    this.form.retailer.$setPristine();
    this.form.retailer.$setUntouched();
  }

  checkDuplicateRetailerId() {
    const isDuplicate = this.newMessageModel.retailerIds.indexOf(this.newMessageModel.retailer) >= 0;
    this.form.retailer.$setValidity('duplicate', !isDuplicate);
  }

  removeRetailerId(index) {
    this.newMessageModel.retailerIds.splice(index, 1);
  }

  addRecipient() {
    this.showEmailValidationErrors = true;
    this.checkDuplicateEmail();
    if (!this.newMessageModel.email || this.form.email.$invalid) {
      return;
    }
    this.newMessageModel.recipients = this.newMessageModel.recipients.concat(this.newMessageModel.email);
    this.newMessageModel.email = '';
    this.showEmailValidationErrors = false;
  }

  checkDuplicateEmail() {
    const isDuplicate = this.newMessageModel.recipients.indexOf(this.newMessageModel.email) >= 0;
    this.form.email.$setValidity('duplicate', !isDuplicate);
  }

  removeRecipient(index) {
    this.newMessageModel.recipients.splice(index, 1);
  }

  showEmptyRecipientsInfo(info) {
    this.Notifications.alertWarning(info);
  }

  submitForm(form) {
    let message = {
      replyEmailAddress: this.newMessageModel.replyEmailAddress,
      [this.newMessageModel.recipientCategoryCode]: 1,
      recipientCategoryCode: this.newMessageModel.recipientCategoryCode,
      subject: this.newMessageModel.subject,
      content: this.newMessageModel.content,
      allowReply: this.newMessageModel.allowReply,
    };
    let sendMessagePromise;

    form.$setSubmitted();
    if (form.$invalid || this.isContentEmpty()) {
      return;
    }

    // eslint-disable-next-line default-case
    switch (this.newMessageModel.recipientCategoryCode) {
      case SELECTED_USERS:
        if (isEmpty(this.newMessageModel.recipients)) {
          this.showEmptyRecipientsInfo(this.messageDefinitionService.get('NO_RECIPIENTS', 'desc'));
          return;
        }
        message = {
          ...message,
          recipientLogins: this.newMessageModel.recipients,
        };
        break;
      case SELECTED_RETAILERS:
        if (isEmpty(this.newMessageModel.retailerIds)) {
          this.showEmptyRecipientsInfo(this.messageDefinitionService.get('NO_RETAILER', 'desc'));
          return;
        }
        message = {
          ...message,
          recipientIds: this.newMessageModel.retailerIds,
        };
        break;
    }

    if (this.canCreateMessageLimitToRetailers) {
      sendMessagePromise = api.createMessageLimitToRetailers(message);
    } else if (this.canCreateMessage) {
      sendMessagePromise = api.createMessage(message);
    }

    sendMessagePromise
      .then(() => {
        this.setDirtyForm(DIRTY_FORM, false);
        this.Notifications.alertSuccess(this.messageDefinitionService.get('MSG_SENT', 'desc'));
      })
      .then(() => {
        this.$state.go('message.list', { skipDirtyForms: true });
      });
  }

  isContentEmpty() {
    return this.newMessageModel.content === '';
  }

  cancelForm() {
    this.$state.go('message.list');
  }
}

export default class CreateMessageComponent {
  constructor() {
    this.controller = CreateMessageController;
    this.template = template;
    return this;
  }
}



// WEBPACK FOOTER //
// ./src/modules/message/components/create-message/create-message-component.js